import { memo } from 'react';
import styled, { css } from 'styled-components';
import { sizeNumeric, colors, borderRadius, spacing } from '@izettle/otto';
import { InputSize } from '../../enumerations/input-size.js';
import { SemanticState } from '../../enumerations/semantic-state.js';

const mixinInputBaseContainerSize = css`
  ${_ref => {
  let {
    isFocused,
    isDisabled,
    semanticState,
    size
  } = _ref;
  const height = {
    [InputSize.Small]: sizeNumeric.xMedium,
    [InputSize.Medium]: sizeNumeric.medium,
    [InputSize.Large]: sizeNumeric.large
  }[size];
  return !isDisabled && (isFocused || semanticState !== SemanticState.Default) ? css`
          border-width: 2px;
          height: ${height}px;
        ` : css`
          border-width: 1px;
          /* stylelint-disable-next-line @izettle/spacing */
          padding: 1px;
          height: ${height}px;
        `;
}}
`;

// Some of the colors below are duplicated and render the logical statements useless
// This is due to the lack of proper semantic tokens for the respective states
// They will be updated in the next release of the Otto library

const semanticColors = {
  [SemanticState.Warning]: css`
    border-color: ${colors.border.warning};
    &:hover {
      border-color: ${colors.border.warning};
    }
  `,
  [SemanticState.Error]: css`
    border-color: ${colors.border.error};
    &:hover {
      border-color: ${colors.border.error};
    }
  `,
  [SemanticState.Success]: css`
    border-color: ${colors.border.success};
    &:hover {
      border-color: ${colors.border.success};
    }
  `,
  [SemanticState.Default]: css`
    border-color: ${colors.border.interactive};
    &:hover {
      /* stylelint-disable-next-line @izettle/border-color */
      border-color: ${colors.icon.secondary};
    }
  `
};
const mixinInputBaseContainerPalette = _ref2 => {
  let {
    semanticState,
    isFocused,
    isDisabled,
    isErrorStateOverriden
  } = _ref2;
  if (isErrorStateOverriden) {
    return semanticColors[SemanticState.Error];
  }
  if (isDisabled) {
    return css`
      cursor: not-allowed;
      border-color: ${colors.border.divider};
      background-color: ${colors.background.groupedPrimary};
    `;
  } else if (isFocused) {
    return css`
      border-color: ${colors.input.border.default};
    `;
  }
  const semanticColorKey = semanticState ?? SemanticState.Default;
  return semanticColors[semanticColorKey];
};
const InputBaseContainer = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "styles__InputBaseContainer",
  componentId: "zettle__sc-1l5gdtq-0"
})(["position:relative;border-style:solid;display:flex;outline:none;border-radius:", ";min-width:100%;max-width:100%;background-color:", ";transition:border-color 0.1s cubic-bezier(0.25,0.1,0.25,1);", " ", " ", ""], borderRadius.medium, colors.background.default, mixinInputBaseContainerSize, mixinInputBaseContainerPalette, _ref3 => {
  let {
    isLoading,
    isDisabled
  } = _ref3;
  return isDisabled ? "cursor: not-allowed;" : isLoading && "cursor: progress;";
}));
const InputBaseContentContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__InputBaseContentContainer",
  componentId: "zettle__sc-1l5gdtq-1"
})(["display:flex;flex:1;position:relative;min-width:0;"]);
const InputBaseControlsContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__InputBaseControlsContainer",
  componentId: "zettle__sc-1l5gdtq-2"
})(["display:flex;"]);
const LoaderContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__LoaderContainer",
  componentId: "zettle__sc-1l5gdtq-3"
})(["height:100%;display:flex;align-items:center;padding-right:", ";"], spacing.small);
const InputPrefix = /*#__PURE__*/styled.div.withConfig({
  displayName: "styles__InputPrefix",
  componentId: "zettle__sc-1l5gdtq-4"
})(["display:none;color:", ";", ""], colors.text.secondary, _ref4 => {
  let {
    isVisible
  } = _ref4;
  return isVisible ? css`
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: ${spacing.xxSmall};
          padding-left: ${spacing.xxSmall};
        ` : css`
          display: none;
        `;
});

export { InputBaseContainer, InputBaseContentContainer, InputBaseControlsContainer, InputPrefix, LoaderContainer };
//# sourceMappingURL=styles.js.map
